'use strict';

Object.defineProperty(exports, '__esModule', {
  value: true
});
var _defineProperty = require('@babel/runtime/helpers/defineProperty');
var _objectWithoutProperties = require('@babel/runtime/helpers/objectWithoutProperties');
var _slicedToArray = require('@babel/runtime/helpers/slicedToArray');
var clickable = require('@chakra-ui/clickable');
var descendant = require('@chakra-ui/descendant');
var hooks = require('@chakra-ui/hooks');
var reactUtils = require('@chakra-ui/react-utils');
var utils = require('@chakra-ui/utils');
var React = require('react');
function _interopDefaultLegacy(e) {
  return e && typeof e === 'object' && 'default' in e ? e : {
    'default': e
  };
}
function _interopNamespace(e) {
  if (e && e.__esModule) return e;
  var n = Object.create(null);
  if (e) {
    Object.keys(e).forEach(function (k) {
      if (k !== 'default') {
        var d = Object.getOwnPropertyDescriptor(e, k);
        Object.defineProperty(n, k, d.get ? d : {
          enumerable: true,
          get: function () {
            return e[k];
          }
        });
      }
    });
  }
  n["default"] = e;
  return Object.freeze(n);
}
var _defineProperty__default = /*#__PURE__*/_interopDefaultLegacy(_defineProperty);
var _objectWithoutProperties__default = /*#__PURE__*/_interopDefaultLegacy(_objectWithoutProperties);
var _slicedToArray__default = /*#__PURE__*/_interopDefaultLegacy(_slicedToArray);
var React__namespace = /*#__PURE__*/_interopNamespace(React);
var _excluded = ["defaultIndex", "onChange", "index", "isManual"],
  _excluded2 = ["isDisabled", "isFocusable", "name"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty__default["default"](target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
var makeSwitchId = function makeSwitchId(id, index) {
  return "".concat(id, "--switch-").concat(index);
};
var getDescendantName = function getDescendantName(descendants, index) {
  return descendants.item(index).node.dataset.name || null;
};

/**
 * Create a context to track descendants and their indices
 */
var _createDescendantCont = descendant.createDescendantContext(),
  _createDescendantCont2 = _slicedToArray__default["default"](_createDescendantCont, 4),
  ContentSwitcherDescendantsProvider = _createDescendantCont2[0],
  useContentSwitcherDescendantsContext = _createDescendantCont2[1],
  useContentSwitcherDescendants = _createDescendantCont2[2],
  useContentSwitcherDescendant = _createDescendantCont2[3];
/**
 * ContentSwitcher hooks that provides all the states, and accessibility
 * helpers to keep all things working properly.
 *
 * Its returned object will be passed unto a Context Provider
 * so all child components can read from it.
 *
 * These hooks are adapted Chakra UI's `useTabs` hooks. They additionally
 * support the passing of a `name` for each switch; which is passed onto the
 * `onChange` callback.
 *
 * The actual tracking and setting of selected switches is done via indexes.
 *
 * The inspiration for this was taken from the Carbon design system's
 * `ContentSwitcher` and Chakra UI's `useTabs` hooks.
 *
 * @see https://github.com/carbon-design-system/carbon/blob/48deedf2fb7a343594ed844d6c852c9108a3eb53/packages/react/src/components/ContentSwitcher/ContentSwitcher.js
 * @see https://github.com/chakra-ui/chakra-ui/blob/d5807a4ef73baf0aa7687a144ca4a4474abe953b/packages/tabs/src/use-tabs.ts
 */
function useContentSwitcher(props) {
  var defaultIndex = props.defaultIndex,
    _onChange = props.onChange,
    index = props.index,
    isManual = props.isManual,
    htmlProps = _objectWithoutProperties__default["default"](props, _excluded);

  /**
   * Think of `useDescendants` as a register for the switch nodes.
   */
  var descendants = useContentSwitcherDescendants();

  /**
   * We use this to keep track of the index of the focused switch.
   *
   * ContentSwitcher can be automatically activated, this means selection follows focus.
   * When we navigate with the arrow keys, we move focus and selection to next/prev switch
   *
   * ContentSwitcher can also be manually activated, this means selection does not follow focus.
   * When we navigate with the arrow keys, we only move focus NOT selection. The user
   * will need not manually activate the switch using `Enter` or `Space`.
   *
   * This is why we need to keep track of the `focusedIndex` and `selectedIndex`
   */
  var _React$useState = React__namespace.useState(defaultIndex !== null && defaultIndex !== void 0 ? defaultIndex : 0),
    _React$useState2 = _slicedToArray__default["default"](_React$useState, 2),
    focusedIndex = _React$useState2[0],
    setFocusedIndex = _React$useState2[1];
  var _useControllableState = hooks.useControllableState({
      defaultValue: defaultIndex !== null && defaultIndex !== void 0 ? defaultIndex : 0,
      value: index,
      onChange: function onChange(i) {
        _onChange === null || _onChange === void 0 ? void 0 : _onChange({
          index: i,
          name: getDescendantName(descendants, i)
        });
      }
    }),
    _useControllableState2 = _slicedToArray__default["default"](_useControllableState, 2),
    selectedIndex = _useControllableState2[0],
    setSelectedIndex = _useControllableState2[1];

  /**
   * Sync focused `index` with controlled `selectedIndex` (which is the `props.index`)
   */
  React__namespace.useEffect(function () {
    if (index != null) {
      setFocusedIndex(index);
    }
  }, [index]);

  /**
   * Generate a unique id or use user-provided id for the ContentSwitcher widget
   */
  var id = hooks.useId(props.id, "contentSwitcher");
  var onKeyDown = React__namespace.useCallback(function (event) {
    var nextTab = function nextTab() {
      var next = descendants.nextEnabled(focusedIndex);
      if (next) utils.focus(next.node);
    };
    var prevTab = function prevTab() {
      var prev = descendants.prevEnabled(focusedIndex);
      if (prev) utils.focus(prev.node);
    };
    var firstTab = function firstTab() {
      var first = descendants.firstEnabled();
      if (first) utils.focus(first.node);
    };
    var lastTab = function lastTab() {
      var last = descendants.lastEnabled();
      if (last) utils.focus(last.node);
    };
    var eventKey = utils.normalizeEventKey(event);
    var keyMap = {
      ArrowLeft: function ArrowLeft() {
        return prevTab();
      },
      ArrowRight: function ArrowRight() {
        return nextTab();
      },
      Home: firstTab,
      End: lastTab
    };
    var action = keyMap[eventKey];
    if (action) {
      event.preventDefault();
      action(event);
    }
  }, [descendants, focusedIndex]);
  return {
    id: id,
    selectedIndex: selectedIndex,
    focusedIndex: focusedIndex,
    setSelectedIndex: setSelectedIndex,
    setFocusedIndex: setFocusedIndex,
    isManual: isManual,
    descendants: descendants,
    htmlProps: _objectSpread(_objectSpread({}, htmlProps), {}, {
      role: 'tablist',
      onKeyDown: utils.callAllHandlers(props.onKeyDown, onKeyDown)
    })
  };
}
var _createContext = reactUtils.createContext({
    name: 'ContentSwitcherContext',
    errorMessage: 'useContentSwitcherContext: `context` is undefined. Seems you forgot to wrap all tabs components within <ContentSwitcher />'
  }),
  _createContext2 = _slicedToArray__default["default"](_createContext, 2),
  ContentSwitcherProvider = _createContext2[0],
  useContentSwitcherContext = _createContext2[1];
/**
 * ContentSwitcher hook to manage each tab button.
 *
 * A tab can be disabled and focusable, or both,
 * hence the use of `useClickable` to handle this scenario
 */
function useSwitch(props) {
  var isDisabled = props.isDisabled,
    isFocusable = props.isFocusable,
    name = props.name,
    htmlProps = _objectWithoutProperties__default["default"](props, _excluded2);
  var _useContentSwitcherCo = useContentSwitcherContext(),
    setSelectedIndex = _useContentSwitcherCo.setSelectedIndex,
    isManual = _useContentSwitcherCo.isManual,
    id = _useContentSwitcherCo.id,
    setFocusedIndex = _useContentSwitcherCo.setFocusedIndex,
    selectedIndex = _useContentSwitcherCo.selectedIndex;
  var _useContentSwitcherDe = useContentSwitcherDescendant({
      disabled: isDisabled && !isFocusable
    }),
    index = _useContentSwitcherDe.index,
    register = _useContentSwitcherDe.register;
  var isSelected = index === selectedIndex;
  var onClick = function onClick() {
    setSelectedIndex(index);
  };
  var onFocus = function onFocus() {
    setFocusedIndex(index);
    var isDisabledButFocusable = isDisabled && isFocusable;
    var shouldSelect = !isManual && !isDisabledButFocusable;
    if (shouldSelect) {
      setSelectedIndex(index);
    }
  };
  var clickableProps = clickable.useClickable(_objectSpread(_objectSpread({}, htmlProps), {}, {
    ref: reactUtils.mergeRefs(register, props.ref),
    isDisabled: isDisabled,
    isFocusable: isFocusable,
    onClick: utils.callAllHandlers(props.onClick, onClick)
  }));
  var type = 'button';
  return _objectSpread(_objectSpread({}, clickableProps), {}, {
    id: makeSwitchId(id, index),
    role: 'tab',
    tabIndex: isSelected ? 0 : -1,
    type: type,
    'aria-selected': isSelected,
    'data-name': name,
    onFocus: isDisabled ? undefined : utils.callAllHandlers(props.onFocus, onFocus)
  });
}
exports.ContentSwitcherDescendantsProvider = ContentSwitcherDescendantsProvider;
exports.ContentSwitcherProvider = ContentSwitcherProvider;
exports.useContentSwitcher = useContentSwitcher;
exports.useContentSwitcherContext = useContentSwitcherContext;
exports.useContentSwitcherDescendant = useContentSwitcherDescendant;
exports.useContentSwitcherDescendants = useContentSwitcherDescendants;
exports.useContentSwitcherDescendantsContext = useContentSwitcherDescendantsContext;
exports.useSwitch = useSwitch;